// Wie wint als jij en een collega dezelfde taak verzetten?
//
// Het scherm ververst zichzelf op de achtergrond. Zonder regels zou dat betekenen dat een
// blok dat je net verschoof onaangekondigd terugspringt naar zijn oude plek — óf omdat
// Airtable onze eigen wijziging nog niet had verwerkt (dan is het terugspringen fout), óf
// omdat iemand anders hem verzette (dan is het terecht, maar dan hoor je te weten waarom).
// Het verschil zit in `LastModified`: is de server ná ons aangeraakt, dan was iemand
// anders aan de beurt.
//
// Puur en zonder React, zodat de regels los te testen zijn.

/** Airtable geeft datumvelden soms met tijd terug; het scherm rekent in hele dagen. */
const normaliseer = (waarde) => (
  typeof waarde === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(waarde) ? waarde.slice(0, 10) : waarde
);

/**
 * Gelijk zoals het scherm het ziet. Leeg, null en false betekenen hier hetzelfde: een
 * afvinkveld dat nooit is aangeraakt komt als `undefined` terug, niet als `false`.
 */
export function gelijkeWaarde(a, b) {
  const x = normaliseer(a);
  const y = normaliseer(b);
  if (x === y) return true;
  const leeg = (w) => w == null || w === '' || w === false;
  if (leeg(x) || leeg(y)) return leeg(x) && leeg(y);
  if (typeof x === 'boolean' || typeof y === 'boolean') return Boolean(x) === Boolean(y);
  return false;
}

/** Is de server ná onze wijziging aangeraakt? Zonder ijkpunt: nee — dan niets aannemen. */
export function isNieuwer(server, ijkpunt) {
  if (!server || !ijkpunt) return false;
  const a = new Date(server).getTime();
  const b = new Date(ijkpunt).getTime();
  return Number.isFinite(a) && Number.isFinite(b) && a > b;
}

/**
 * Wat er met onze optimistische overlays moet gebeuren na een verse serverronde.
 *
 * @param takenPerId  id → verse taak van de server
 * @param overlays    id → velden die wij optimistisch hebben gezet
 * @param basis       id → `lastModified` zoals die was toen wij onze wijziging deden
 * @returns `{ weg, conflicten }` — `weg` zijn de overlays die eraf mogen, `conflicten`
 *          zijn de taken waarover je iets moet zeggen omdat een ander ze verzette.
 */
export function beoordeelOverlays({ takenPerId = {}, overlays = {}, basis = {} } = {}) {
  const weg = [];
  const conflicten = [];

  Object.entries(overlays).forEach(([id, velden]) => {
    const taak = takenPerId[id];
    // Niet meer in beeld (buiten het venster verzet, of verwijderd): een overlay op niets
    // houdt alleen een verouderde waarde in leven.
    if (!taak) { weg.push(id); return; }

    const bij = Object.entries(velden || {})
      .every(([veld, waarde]) => gelijkeWaarde(taak[veld], waarde));
    // De server is ons ingehaald — overlay overbodig, en niets om over te melden.
    if (bij) { weg.push(id); return; }

    if (isNieuwer(taak.lastModified, basis[id])) {
      weg.push(id);
      conflicten.push({ id, naam: taak.name, door: taak.lastModifiedBy || '' });
      return;
    }
    // Anders: Airtable heeft onze wijziging nog niet terug. Overlay laten staan, want
    // die is op dit moment dichter bij de waarheid dan wat de server teruggeeft.
  });

  return { weg, conflicten };
}

/** De melding bij een conflict. Zonder naam blijft het "iemand" — nooit stil terugklappen. */
export const conflictMelding = ({ naam, door }) =>
  `${naam || 'Een taak'} is ondertussen aangepast door ${door || 'iemand anders'}`;
